/**
 * useUzaverkaDraft — perzistence rozepsané uzávěrky pokladny v localStorage.
 *
 * Klíč: `mojesmena.uzaverkaDraft.{smenaId}` (per-směna, žádné kolize mezi nimi).
 * Schéma:
 *   {
 *     smenaId: number,         // sanity check — pokud nesedí, draft reset
 *     D: string,               // terminal částka (text z inputu)
 *     denom: { 2000: 0, 1000: 1, ... },
 *     komentar: string,
 *     fotoPath: string | null, // path z upload endpointu
 *     savedAt: ISO timestamp,
 *   }
 *
 * Po úspěšném submit volá konzument `clearDraft()` aby smazal záznam.
 * Žádný backend save — task spec explicitně chce jen localStorage.
 */
import { useState, useEffect, useRef, useCallback } from 'react';

const STORAGE_PREFIX = 'mojesmena.uzaverkaDraft';
const DEBOUNCE_MS = 300;

const EMPTY_DRAFT = {
    smenaId: null,
    D: '',
    denom: {},
    // null = denom mode (E se počítá z denom mapy), number = manual mode (uživatel
    // zadal celkovou hotovost ručně přes klávesnici, denom mapa se ignoruje).
    manualE: null,
    komentar: '',
    fotoPath: null,
    savedAt: null,
};

function key(smenaId) {
    return `${STORAGE_PREFIX}.${smenaId}`;
}

export function useUzaverkaDraft(smenaId) {
    const [draft, setDraftState] = useState({ ...EMPTY_DRAFT, smenaId });
    const debounceRef = useRef(null);
    const mountedRef = useRef(false);

    // Mount: načti z localStorage. Pokud uložená smenaId nesedí (typicky když
    // se sheet otevře pro jinou směnu než předtím), reset na prázdný draft.
    useEffect(() => {
        if (!smenaId) return;
        try {
            const raw = localStorage.getItem(key(smenaId));
            if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed?.smenaId === smenaId) {
                    setDraftState({ ...EMPTY_DRAFT, ...parsed, smenaId });
                    mountedRef.current = true;
                    return;
                }
            }
        } catch (_) { /* corrupted JSON → reset */ }
        setDraftState({ ...EMPTY_DRAFT, smenaId });
        mountedRef.current = true;
    }, [smenaId]);

    // Debounced write — během rychlého psaní v inputu nezahltíme localStorage
    // (~10× /s pri psaní), ale po 300 ms zaspaní zachytíme stabilní stav.
    const flushToStorage = useCallback((next) => {
        if (!smenaId) return;
        try {
            localStorage.setItem(key(smenaId), JSON.stringify({
                ...next,
                smenaId,
                savedAt: new Date().toISOString(),
            }));
        } catch (_) { /* localStorage plné nebo zakázané — silent fail */ }
    }, [smenaId]);

    const setDraft = useCallback((patch) => {
        setDraftState(prev => {
            const next = typeof patch === 'function' ? patch(prev) : { ...prev, ...patch };
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => flushToStorage(next), DEBOUNCE_MS);
            return next;
        });
    }, [flushToStorage]);

    const clearDraft = useCallback(() => {
        if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
        try { localStorage.removeItem(key(smenaId)); } catch (_) { /* */ }
        setDraftState({ ...EMPTY_DRAFT, smenaId });
    }, [smenaId]);

    return { draft, setDraft, clearDraft };
}
